import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { ArrowLeftRight, Boxes, History, Loader2, PenLine, RefreshCw } from 'lucide-react';
import * as XLSX from 'xlsx';
import { PageHeader } from '../../../../components/ui/PageHeader';
import { DataTable } from '../../../../components/ui/DataTable';
import { Button } from '../../../../components/ui/Button';
import { ToneBadge } from '../../../../components/ui/ToneBadge';
import { useNotice } from '../../../../hooks/useNotice';
import { StockTotals } from '../../../../components/warehouses/StockTotals';
import { InventoryFilters } from './components/InventoryFilters';
import { TransferStockModal } from './modals/TransferStockModal';
import { AdjustStockModal } from './modals/AdjustStockModal';
import { ShopifyInventoryModal } from './modals/ShopifyInventoryModal';
import { fetchStock } from '../../../../features/stock/stockThunks';
import { fetchStockSyncChannels } from '../../../../features/stockSync/stockSyncThunks';
import {
  stockSyncChannelSelected,
  stockSyncLeft,
  stockSyncWatched,
} from '../../../../features/stockSync/stockSyncSlice';
import { selectIsRunActive, selectUsableChannels } from '../../../../features/stockSync/stockSyncSelectors';
import { RUN_KIND, STOCK_SYNC_WATCHER } from '../../../../features/stockSync/stockSyncConstants';
import { pickDefaultChannelId } from '../../../../features/stockSync/stockSyncUtils';
import { setStockFilters } from '../../../../features/stock/stockSlice';
import { selectCanSeeStockDetail } from '../../../../features/stock/stockSelectors';
import { selectCanWms } from '../../../../features/auth/authSelectors';
import { buildStockExportRows, stockExportFileName } from '../../../../features/stock/stockExport';
import { ALL_FILTER, STOCK_GROUP_BY } from '../../../../features/stock/stockConstants';
import {
  selectCanOperateWarehouse,
  selectCurrentWarehouse,
  selectWarehouseClientOptions,
} from '../../../../features/warehouseContext/warehouseContextSlice';
import {
  STOCK_STATE_COLORS,
  formatQuantity,
  stockStateLabel,
} from '../../../../utils/inventoryLabels';

const INVENTORY_PATH = '/warehouse/inventory';

/** Counting and adjusting are the same permission: whoever may count may correct. */
const selectCanAdjustStock = selectCanWms('count:perform');

/** The button stays usable while a load runs: it opens the dialog that follows it. */
const SyncingIcon = ({ className, ...props }) => <Loader2 {...props} className={clsx(className, 'animate-spin')} />;

/** Green when there is some left, red when more was promised than there is. */
const availableTone = (value) => {
  const available = Number(value);
  if (available < 0) return 'danger';
  return available > 0 ? 'success' : 'neutral';
};

const Quantity = ({ value, uom }) => (
  <span className="font-semibold tabular-nums">
    {formatQuantity(value)}
    <span className="ml-1 text-xs font-normal text-text-muted">{uom}</span>
  </span>
);

/**
 * `/warehouse/inventory`: the balances of the kardex.
 *
 * Two views of the same stock — consolidated by SKU, and one row per location —
 * with three figures above that cover the whole filter. Every row leads to the
 * movements that explain its number.
 *
 * Whoever may see locations may also move stock between them ("Guardar /
 * mover"), which needs one concrete warehouse with somebody operating in it.
 * Whoever may count may also add or remove units by hand, with a reason
 * ("Ajustar", on each row): the door for losses, breakage and counts that did
 * not match, which no receipt or order explains.
 */
export const Inventory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { rows, totalCount, totals, groupedBy, loading, error, filters } = useSelector(
    (state) => state.stock
  );
  const warehouse = useSelector(selectCurrentWarehouse);
  const clientOptions = useSelector(selectWarehouseClientOptions);
  const canSeeDetail = useSelector(selectCanSeeStockDetail);
  const canOperate = useSelector(selectCanOperateWarehouse);
  const canAdjust = useSelector(selectCanAdjustStock);
  const shopifyChannels = useSelector(selectUsableChannels);
  const shopifySelectedId = useSelector((state) => state.stockSync.selectedId);
  const comparing = useSelector(selectIsRunActive(RUN_KIND.STOCK_PREVIEW));
  const aligning = useSelector(selectIsRunActive(RUN_KIND.STOCK_ALIGNMENT));
  const shopifyRunning = comparing || aligning;

  const [searchInput, setSearchInput] = useState(filters.search);
  /** `null` closed, `{ row: null }` from the header, `{ row }` from a row. */
  const [transfer, setTransfer] = useState(null);
  /** The row being adjusted, or `null`. */
  const [adjusting, setAdjusting] = useState(null);
  const [isShopifyOpen, setIsShopifyOpen] = useState(false);
  const setNotice = useNotice('inventory-notice');

  useEffect(() => {
    dispatch(stockSyncWatched(STOCK_SYNC_WATCHER.INVENTORY));
    return () => dispatch(stockSyncLeft(STOCK_SYNC_WATCHER.INVENTORY));
  }, [dispatch]);

  // The stores that sell the stock of the warehouse in use.
  useEffect(() => {
    dispatch(fetchStockSyncChannels({ warehouseId: warehouse?.id ?? null }));
  }, [dispatch, warehouse?.id]);

  // Debounce for search
  useEffect(() => {
    const handler = setTimeout(() => {
      const search = searchInput.trim();
      if (search !== filters.search) dispatch(setStockFilters({ search }));
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput, filters.search, dispatch]);

  // Fetch when the warehouse in use or a filter changes
  useEffect(() => {
    dispatch(fetchStock());
  }, [
    dispatch,
    warehouse?.id,
    filters.company,
    filters.state,
    filters.search,
    filters.includeZero,
    filters.view,
    filters.page,
    filters.rowsPerPage,
  ]);

  const handleFilterChange = useCallback(
    (patch) => dispatch(setStockFilters(patch)),
    [dispatch]
  );


  // The dialog picks among the products on screen: the ones with stock here,
  // which is exactly what there is to move.
  const productOptions = useMemo(() => {
    const byId = new Map();
    rows.forEach((row) => {
      if (!byId.has(row.product)) {
        byId.set(row.product, {
          value: row.product,
          label: `${row.product_sku} — ${row.product_name}`,
          sku: row.product_sku,
        });
      }
    });
    return [...byId.values()];
  }, [rows]);

  // Seen but not usable right now: the button stays and says why.
  const transferBlocker = (() => {
    if (!warehouse) return 'Elige arriba una bodega: mover stock pasa en un lugar concreto';
    if (!canOperate) return `${warehouse.name} no tiene clientes con contrato activo`;
    if (rows.length === 0) return 'No hay stock que mover con estos filtros';
    return null;
  })();

  const openShopifySync = () => {
    const companyId = filters.company === ALL_FILTER ? null : filters.company;
    dispatch(stockSyncChannelSelected(pickDefaultChannelId(shopifyChannels, { selectedId: shopifySelectedId, companyId })));
    setIsShopifyOpen(true);
  };

  const handleExport = () => {
    const sheet = XLSX.utils.json_to_sheet(buildStockExportRows(rows, groupedBy));
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Inventario');
    XLSX.writeFile(book, stockExportFileName(warehouse, groupedBy));
  };

  // The columns follow what the server grouped, not what the switch says: while
  // the other view is still on its way, the rows on screen are the old ones.
  const isDetail = groupedBy === null;

  // Somebody who only sees one client does not need a column repeating its
  // name on every row; a lot column full of dashes says nothing either.
  const showClient = clientOptions.length > 1;
  const showLot = rows.some((row) => Boolean(row.lot));

  const clientColumn = {
    header: 'CLIENTE',
    cell: (row) => <span className="text-text-secondary">{row.company_name || '-'}</span>,
  };

  // SKU and name in one cell: the same thing looked at in two ways.
  const productColumn = {
    header: 'PRODUCTO',
    cell: (row) => (
      <div className="flex flex-col max-w-[340px]">
        <span className="font-bold font-mono text-ninja-dark dark:text-white">
          {row.product_sku}
        </span>
        <span className="text-xs text-text-muted mt-0.5 truncate" title={row.product_name}>
          {row.product_name}
        </span>
      </div>
    ),
  };

  const lotColumn = {
    header: 'LOTE',
    cell: (row) => <span className="font-mono">{row.lot || '-'}</span>,
  };

  const actionsColumn = {
    header: 'ACCIONES',
    cell: (row) => (
      <div className="flex items-center justify-end gap-1">
        {/* Only from a row that says where the stock is, and only stock that
            can leave: reserved units are spoken for. */}
        {canSeeDetail && canOperate && row.slot && row.state === 'available' && (
          <Button
            variant="subtle"
            size="sm"
            iconOnly
            icon={ArrowLeftRight}
            title="Mover desde esta ubicación"
            aria-label={`Mover ${row.product_sku} desde ${row.slot_name}`}
            data-testid="inventory-transfer-row"
            onClick={() => setTransfer({ row })}
          />
        )}
        {/* In one concrete warehouse that somebody operates: an adjustment is
            a movement, and a movement happens in a place. */}
        {canAdjust && canOperate && warehouse && (
          <Button
            variant="subtle"
            size="sm"
            iconOnly
            icon={PenLine}
            title="Ajustar: añadir o quitar unidades con un motivo"
            aria-label={`Ajustar ${row.product_sku}`}
            data-testid="inventory-adjust-row"
            onClick={() => setAdjusting(row)}
          />
        )}
        <Button
          variant="subtle"
          size="sm"
          iconOnly
          icon={History}
          title="Ver los movimientos que explican este saldo"
          aria-label={`Ver movimientos de ${row.product_sku}`}
          data-testid="inventory-kardex-open"
          onClick={() => navigate(`${INVENTORY_PATH}/${row.product}`)}
        />
      </div>
    ),
  };

  const consolidatedColumns = [
    ...(showClient ? [clientColumn] : []),
    productColumn,
    ...(groupedBy === STOCK_GROUP_BY.SKU_LOT ? [lotColumn] : []),
    {
      header: 'DISPONIBLE',
      cell: (row) => (
        <ToneBadge tone={availableTone(row.available)} testId="inventory-available">
          {formatQuantity(row.available)}
        </ToneBadge>
      ),
    },
    {
      header: 'RESERVADO',
      cell: (row) => (
        <ToneBadge
          tone={Number(row.reserved) > 0 ? 'warning' : 'neutral'}
          title="Prometido a pedidos que todavía no se pickean. Al pickear baja, junto con el total."
          testId="inventory-reserved"
        >
          {formatQuantity(row.reserved ?? 0)}
        </ToneBadge>
      ),
    },
    {
      header: 'TOTAL',
      cell: (row) => <Quantity value={row.total} uom={row.base_uom} />,
    },
    actionsColumn,
  ];

  const detailColumns = [
    ...(showClient ? [clientColumn] : []),
    productColumn,
    {
      header: 'UBICACIÓN',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-mono text-ninja-dark dark:text-white">
            {row.slot_name || 'Sin ubicar'}
          </span>
          <span className="text-xs text-text-muted mt-0.5">{row.warehouse_name}</span>
        </div>
      ),
    },
    ...(showLot ? [lotColumn] : []),
    {
      header: 'ESTADO',
      cell: (row) => (
        <ToneBadge tone={STOCK_STATE_COLORS[row.state]} testId="inventory-state-badge">
          {stockStateLabel(row.state)}
        </ToneBadge>
      ),
    },
    {
      header: 'CANTIDAD',
      cell: (row) => <Quantity value={row.quantity} uom={row.base_uom} />,
    },
    actionsColumn,
  ];

  // An empty table has to say why: empty looks the same as broken.
  const isFiltering =
    filters.search !== '' || filters.company !== ALL_FILTER || filters.state !== ALL_FILTER;
  const emptyMessage = isFiltering
    ? 'No hay stock que coincida con estos filtros.'
    : `Todavía no hay stock en ${
        warehouse ? warehouse.name : 'tus bodegas'
      }. La mercadería entra por una recepción.`;

  return (
    <div data-testid="inventory-page" className="space-y-6 animate-in fade-in duration-300">
      <PageHeader
        icon={Boxes}
        title="Inventario"
        description="Los saldos de tu stock. Cada fila abre los movimientos que explican su número."
        actions={
          <>
            {/* In the header, not the filter strip: the strip has no room left at a laptop's width. */}
            {shopifyChannels.length > 0 && (
              <Button
                variant="ghost"
                icon={shopifyRunning ? SyncingIcon : RefreshCw}
                onClick={openShopifySync}
                data-testid="inventory-shopify-sync"
                data-running={String(shopifyRunning)}
              >
                {aligning ? 'Sincronizando stock…' : comparing ? 'Comparando con Shopify…' : 'Sincronizar con Shopify'}
              </Button>
            )}
            {canSeeDetail && (
              <Button
                variant="primary"
                icon={ArrowLeftRight}
                disabled={Boolean(transferBlocker)}
                title={
                  transferBlocker ||
                  'Llevar stock de una ubicación a otra, o guardar lo que está en recepción'
                }
                onClick={() => setTransfer({ row: null })}
                data-testid="inventory-transfer"
              >
                Guardar / mover
              </Button>
            )}
          </>
        }
      />

      <InventoryFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        searchInput={searchInput}
        onSearchInputChange={setSearchInput}
        clientOptions={clientOptions}
        canSeeDetail={canSeeDetail}
        onExport={handleExport}
        canExport={rows.length > 0}
      />

      {/* Not shown next to a failure: a row of zeros reads exactly like "there
          is no stock", and the screen would keep looking authoritative. */}
      {!error && <StockTotals totals={totals} />}

      <DataTable
        columns={isDetail ? detailColumns : consolidatedColumns}
        data={rows}
        // A consolidated row has no id of its own: its identity is the product.
        idAccessor={isDetail ? 'id' : 'product'}
        totalItems={totalCount}
        currentPage={filters.page}
        itemsPerPage={filters.rowsPerPage}
        isLoading={loading}
        error={error}
        onRetry={() => dispatch(fetchStock())}
        itemLabel={isDetail ? 'registros' : 'SKUs'}
        emptyMessage={emptyMessage}
        onPageChange={(page) => dispatch(setStockFilters({ page }))}
        onRowsPerPageChange={(rowsPerPage) => dispatch(setStockFilters({ rowsPerPage }))}
      />

      <TransferStockModal
        isOpen={Boolean(transfer)}
        onClose={() => setTransfer(null)}
        warehouse={warehouse}
        productOptions={productOptions}
        initial={transfer?.row ?? null}
        onDone={(message) => setNotice({ type: 'success', message })}
      />

      <AdjustStockModal
        isOpen={Boolean(adjusting)}
        onClose={() => setAdjusting(null)}
        warehouse={warehouse}
        row={adjusting}
        onDone={(message) => setNotice({ type: 'success', message })}
      />

      <ShopifyInventoryModal isOpen={isShopifyOpen} onClose={() => setIsShopifyOpen(false)} />
    </div>
  );
};
